import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { VpVendorName } from '../models/vp-vendor-name.model';
import {
  VENDOR_SEARCH_TOP,
  vendorSearchODataFilter,
} from '../utils/vendor-search.utils';

/** WI 34964: Viewpoint AP vendor search for the `typeahead-vendor` dynamic control. */
@Injectable({
  providedIn: 'root',
})
export class SearchVpVendorService {
  private readonly http: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.http = clientFactory.createHttpClient(
      environment.urls.toolTrackerAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /** Vendors in `vendorGroup` matching `query`. No vendor group returns an empty list, never every group. */
  search(
    query: string,
    vendorGroup: number | null
  ): Observable<Array<VpVendorName>> {
    if (vendorGroup == null) {
      return of([]);
    }
    let params = new HttpParams()
      .set('$top', VENDOR_SEARCH_TOP)
      .set('$orderby', 'Name');
    const filter = vendorSearchODataFilter(query);
    if (filter) {
      params = params.set('$filter', filter);
    }
    return this.http.get<Array<VpVendorName>>(
      `/api/vendor/vendornames-odata/${vendorGroup}`,
      { params }
    );
  }

  /**
   * `search`, once `vendorGroup$` first emits. A form's reopen prefill can search before the
   * user's profile loads; waiting keeps that search from failing closed and dropping the saved vendor.
   */
  searchInGroup(
    query: string,
    vendorGroup$: Observable<number | null>
  ): Observable<Array<VpVendorName>> {
    return vendorGroup$.pipe(
      take(1),
      switchMap((group) => this.search(query, group))
    );
  }

  /**
   * WI 34965: the vendor's Viewpoint contact (bAPVM keeps one per vendor). An empty string when the
   * vendor has no contact, which clears a stale auto-filled contact (Task 35033); null when there
   * is no vendor or the lookup fails, which leaves the field alone.
   */
  contactFor(
    vendorGroup: number | null | undefined,
    vendor: number | null | undefined
  ): Observable<string | null> {
    if (vendorGroup == null || vendor == null) {
      return of(null);
    }
    return this.http
      .get<{ contact?: string | null }>(
        `/api/vendor/vendordata/${vendorGroup}/${vendor}`
      )
      .pipe(
        map((data) => (data == null ? null : (data.contact ?? '').trim())),
        catchError(() => of(null))
      );
  }
}
